// Icon utility helper — Phosphor Icons for DOM and canvas.
// Path data from phosphor-icons-loader (public/phosphor-nodes-*.json).

import { renderIconOnCanvas as renderIconOnCanvasFromCanvas, getIconDefinition } from './canvas-icons';
import { getPhosphorNodesOutline, getPhosphorNodesFilled } from './phosphor-icons-loader';
import { iconNameMap, type IconName } from './iconsUiRegistry';
import type { NodeIconIdentifier } from './iconsNodeRegistry';

export type { IconName } from './iconsUiRegistry';
export type { NodeIconIdentifier } from './iconsNodeRegistry';

function getPhosphorNodes(variant: 'line' | 'filled'): Record<string, unknown> {
  return variant === 'filled' ? getPhosphorNodesFilled() : getPhosphorNodesOutline();
}

// Helper to get icon path data from Phosphor nodes (same shape: [["path", {d}], ...])
function getIconPathData(phosphorIconName: string, variant: 'line' | 'filled'): Array<{ d: string }> {
  const nodes = getPhosphorNodes(variant);
  const iconData = nodes[phosphorIconName];
  if (!iconData || !Array.isArray(iconData)) {
    return [];
  }
  return (iconData as Array<['path', { d: string }]>)
    .filter((node): node is ['path', { d: string }] =>
      Array.isArray(node) && node[0] === 'path' && typeof node[1]?.d === 'string')
    .map((node) => ({ d: node[1].d }));
}

/**
 * Creates an icon element that can be inserted into DOM
 * @param name Icon name
 * @param size Icon size in pixels (default: 16) - kept for API compatibility, size is controlled via CSS
 * @param color Icon color (default: currentColor)
 * @param className Optional CSS class name
 * @param variant Icon variant - if not provided, will be auto-selected based on icon name
 * @returns SVG element
 */
export function createIconElement(
  name: IconName,
  _size: number = 16,
  color: string = 'currentColor',
  className?: string,
  variant?: 'line' | 'filled'
): SVGElement {
  // Auto-select variant if not provided
  const selectedVariant = variant ?? getIconVariantForIconName(name);
  
  const phosphorIconName = iconNameMap[name];
  if (!phosphorIconName) {
    throw new Error(`Unknown icon: ${name}`);
  }

  const paths = getIconPathData(phosphorIconName, selectedVariant);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  // Phosphor path data is 256×256; viewBox keeps aspect ratio, size via CSS.
  // Phosphor icons are authored as filled paths in both regular and fill weights; we always fill.
  svg.setAttribute('viewBox', '0 0 256 256');
  svg.setAttribute('fill', color);
  svg.setAttribute('stroke', 'none');
  
  if (className) {
    svg.setAttribute('class', className);
  }
  
  // Set inline styles (excluding width/height - controlled via CSS)
  svg.style.display = 'block';
  svg.style.flexShrink = '0';
  svg.style.verticalAlign = 'middle';
  svg.style.pointerEvents = 'none';
  
  // Add path elements from icon data
  if (paths.length > 0) {
    paths.forEach((pathData) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pathData.d);
      path.setAttribute('fill', color);
      path.setAttribute('stroke', 'none');
      svg.appendChild(path);
    });
  } else {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    path.setAttribute('cx', '128');
    path.setAttribute('cy', '128');
    path.setAttribute('r', '64');
    path.setAttribute('fill', selectedVariant === 'filled' ? color : 'none');
    path.setAttribute('stroke', selectedVariant === 'filled' ? 'none' : color);
    svg.appendChild(path);
  }

  return svg;
}

/**
 * Returns icon SVG as HTML string for use with {@html} in Svelte
 */
export function getIconHtml(
  name: IconName,
  color: string = 'currentColor',
  className?: string,
  variant?: 'line' | 'filled'
): string {
  try {
    const svg = createIconElement(name, 16, color, className ?? 'icon', variant);
    return svg.outerHTML;
  } catch {
    return '';
  }
}

/**
 * Returns node icon SVG as HTML string for use with {@html} in Svelte
 */
export function getNodeIconHtml(
  identifier: NodeIconIdentifier | string,
  color: string = 'currentColor',
  className?: string,
  variant?: 'line' | 'filled'
): string {
  try {
    const svg = createNodeIconElement(identifier, 24, color, className ?? 'item-icon', variant);
    return svg.outerHTML;
  } catch {
    return '';
  }
}

/**
 * Centralized icon variant selection
 * Gets the variant (line or filled) from the centralized icon registry.
 * This ensures consistent icon styling across the entire application.
 * 
 * All icon definitions (including variant) are in canvas-icons.ts iconRegistry.
 * This function simply retrieves the variant from that registry.
 * 
 * @param iconIdentifier Node icon identifier
 * @returns 'line' or 'filled' variant (defaults to 'line' if icon not found)
 */
export function getIconVariant(iconIdentifier: NodeIconIdentifier | string): 'line' | 'filled' {
  const iconDef = getIconDefinition(iconIdentifier);
  return iconDef?.variant ?? 'line';
}

/**
 * Centralized icon variant selection for IconName types (UI icons)
 * Determines whether an icon should use 'line' or 'filled' style based on the icon name.
 * 
 * @param iconName Icon name
 * @returns 'line' or 'filled' variant
 */
export function getIconVariantForIconName(iconName: IconName): 'line' | 'filled' {
  // Icons that should be filled
  const filledIcons = new Set<IconName>([
    'sparkles',
    'layout-grid',
    'layout-sidebar-left-collapse',
  ]);
  
  if (filledIcons.has(iconName)) {
    return 'filled';
  }
  
  // Default to line for most UI icons
  return 'line';
}

/**
 * Gets a CSS custom property value as a number (for ratios) or string
 * @param propertyName CSS custom property name (e.g., '--icon-stroke-width')
 * @param fallback Fallback value if property is not found
 * @returns Parsed numeric value or fallback
 */
function getIconToken(propertyName: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(propertyName)
    .trim();
  if (!value) return fallback;
  
  // Remove 'px' suffix if present and parse as number
  const numValue = parseFloat(value.replace('px', ''));
  return isNaN(numValue) ? fallback : numValue;
}

/**
 * Gets icon style tokens from CSS custom properties
 * Returns standardized ratios and values for consistent icon rendering
 */
function getIconStyle(size: number) {
  // Get numeric ratios from CSS tokens (these are unitless ratios)
  const ratios = {
    base: getIconToken('--icon-size-base', 1),
    large: getIconToken('--icon-size-large', 0.9),
    medium: getIconToken('--icon-size-medium', 0.8),
    small: getIconToken('--icon-size-small', 0.6),
    tiny: getIconToken('--icon-size-tiny', 0.4),
    half: getIconToken('--icon-spacing-half', 0.5),
    quarter: getIconToken('--icon-spacing-quarter', 0.25),
    eighth: getIconToken('--icon-spacing-eighth', 0.125),
    primary: getIconToken('--icon-element-primary', 0.8),
    secondary: getIconToken('--icon-element-secondary', 0.6),
    tertiary: getIconToken('--icon-element-tertiary', 0.4),
    accent: getIconToken('--icon-element-accent', 0.3),
    gridSpacing: getIconToken('--icon-grid-spacing', 0.25),
    arrowSize: getIconToken('--icon-arrow-size', 0.5),
    arrowWidth: getIconToken('--icon-arrow-width', 0.3),
    circleOuter: getIconToken('--icon-circle-outer', 0.8),
    circleInner: getIconToken('--icon-circle-inner', 0.5),
    circleCenter: getIconToken('--icon-circle-center', 0.3),
    lineLength: getIconToken('--icon-line-length', 0.7),
    lineOffset: getIconToken('--icon-line-offset', 0.15),
  };

  // Get stroke width (in pixels)
  const strokeWidth = getIconToken('--icon-stroke-width', 2);
  
  return {
    size,
    halfSize: size * ratios.half,
    quarterSize: size * ratios.quarter,
    eighthSize: size * ratios.eighth,
    strokeWidth,
    ratios,
    // Computed sizes for convenience
    primary: size * ratios.primary,
    secondary: size * ratios.secondary,
    tertiary: size * ratios.tertiary,
    accent: size * ratios.accent,
    large: size * ratios.large,
    medium: size * ratios.medium,
    small: size * ratios.small,
    tiny: size * ratios.tiny,
  };
}

/**
 * Renders an icon on a canvas context.
 * Delegates to canvas-icons (iconRegistry: phosphorIconName + variant).
 * 
 * @param ctx Canvas rendering context
 * @param iconName Icon identifier
 * @param x Center X position
 * @param y Center Y position
 * @param size Icon size in pixels
 * @param color Icon color
 */
export function renderIconOnCanvas(
  ctx: CanvasRenderingContext2D,
  iconName: NodeIconIdentifier | string,
  x: number,
  y: number,
  size: number,
  color: string
): void {
  // Calculate stroke width from CSS tokens for consistent rendering
  const style = getIconStyle(size);
  
  // Delegate to the centralized renderIconOnCanvas which uses the iconRegistry
  // This ensures both DOM and Canvas use the same icon definitions (name + variant)
  renderIconOnCanvasFromCanvas(ctx, iconName, x, y, size, color, style.strokeWidth);
}

/**
 * Creates an icon element for a node icon identifier (can be used in DOM)
 * This is similar to createIconElement but works with NodeIconIdentifier instead of IconName
 * @param iconIdentifier Node icon identifier
 * @param size Icon size in pixels (default: 16) - kept for API compatibility, size is controlled via CSS
 * @param color Icon color (default: currentColor)
 * @param className Optional CSS class name
 * @param variant Icon variant - if not provided, will be auto-selected based on icon identifier
 * @returns SVG element
 */
export function createNodeIconElement(
  iconIdentifier: NodeIconIdentifier | string,
  _size: number = 16,
  color: string = 'currentColor',
  className?: string,
  variant?: 'line' | 'filled'
): SVGElement {
  // Get icon definition from centralized registry
  const iconDef = getIconDefinition(iconIdentifier);
  
  const selectedVariant = variant ?? iconDef?.variant ?? 'line';
  const phosphorIconName = iconDef?.phosphorIconName;

  if (!iconDef || !phosphorIconName) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 256 256');
    // Apply base icon class and any additional classes
    const classes = className ? `icon ${className}` : 'icon';
    svg.setAttribute('class', classes);
    
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '128');
    circle.setAttribute('cy', '128');
    circle.setAttribute('r', '64');
    circle.setAttribute('fill', selectedVariant === 'filled' ? color : 'none');
    circle.setAttribute('stroke', selectedVariant === 'filled' ? 'none' : color);
    svg.appendChild(circle);
    return svg;
  }

  const paths = getIconPathData(phosphorIconName, selectedVariant);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 256 256');
  // Phosphor icons are authored as filled paths in both regular and fill weights; we always fill.
  svg.setAttribute('fill', color);
  svg.setAttribute('stroke', 'none');
  
  // Apply base icon class and any additional classes
  const classes = className ? `icon ${className}` : 'icon';
  svg.setAttribute('class', classes);
  
  // Add path elements from icon data
  if (paths.length > 0) {
    paths.forEach((pathData) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pathData.d);
      path.setAttribute('fill', color);
      path.setAttribute('stroke', 'none');
      svg.appendChild(path);
    });
  } else {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    path.setAttribute('cx', '128');
    path.setAttribute('cy', '128');
    path.setAttribute('r', '64');
    path.setAttribute('fill', selectedVariant === 'filled' ? color : 'none');
    path.setAttribute('stroke', selectedVariant === 'filled' ? 'none' : color);
    svg.appendChild(path);
  }

  return svg;
}

// Re-export canvas icon utilities for convenience
export {
  preloadIcons,
  clearIconCache
} from './canvas-icons';
